// NoticeBoard.jsx
// ─ Premium Notice Board Section
// ─ Fixed tel links + cleaned modal/footer
// ─ Outdoor / modern glass UI
// ─ Updated: website link button in modal when notice.link exists

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiChevronRight,
  FiX,
  FiCalendar,
  FiClock,
  FiExternalLink,
} from 'react-icons/fi';

import { NOTICES, NOTICE_TYPES } from '../../data/notices';

/* ── helpers ─────────────────────────────────────────────── */
function formatDateStr(dateStr, isJa) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isJa) {
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  }
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function isExpiringSoon(expires) {
  if (!expires) return false;
  const diff = new Date(expires) - new Date();
  return diff > 0 && diff < 1000 * 60 * 60 * 24 * 7;
}

function isExpired(expires) {
  if (!expires) return false;
  return new Date(expires) < new Date();
}

/* ── TypeTag ─────────────────────────────────────────────── */
function TypeTag({ type, isJa, small = false }) {
  const cfg = NOTICE_TYPES[type];
  if (!cfg) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 font-extrabold rounded-full whitespace-nowrap backdrop-blur-md
      ${small ? 'text-[9px] px-2.5 py-1' : 'text-[10px] px-3 py-1.5'}`}
      style={{
        background: cfg.bg,
        color: cfg.color,
        border: `1.5px solid ${cfg.border}`,
      }}
    >
      {cfg.emoji} {isJa ? cfg.labelJa : cfg.labelEn}
    </span>
  );
}

/* ── NoticeCard ──────────────────────────────────────────── */
function NoticeCard({ notice, isJa, onClick }) {
  const cfg = NOTICE_TYPES[notice.type];
  const expired = isExpired(notice.expires);
  const soon    = isExpiringSoon(notice.expires);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: expired ? 0.5 : 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      whileHover={!expired ? { y: -4 } : {}}
      onClick={() => !expired && onClick(notice)}
      className={`
        relative bg-white/75 backdrop-blur-xl rounded-[24px]
        border overflow-hidden transition-all duration-300
        ${expired
          ? 'cursor-default'
          : 'cursor-pointer hover:shadow-2xl hover:shadow-[#1f4d2b]/10'}
        ${notice.important ? 'shadow-lg' : 'shadow-sm'}
      `}
      style={{
        borderColor: notice.important
          ? `${cfg?.color}55`
          : 'rgba(223,209,188,.7)',
      }}
    >
      {/* top stripe */}
      {notice.important && !expired && (
        <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: cfg?.color }} />
      )}

      {/* glow */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ background: `radial-gradient(circle at top right, ${cfg?.color}, transparent 45%)` }}
      />

      <div className="relative z-10 p-5">

        {/* top row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <TypeTag type={notice.type} isJa={isJa} small />
          <div className="flex items-center gap-1.5 shrink-0">
            {soon && !expired && (
              <span className="text-[9px] font-extrabold text-red-500 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                {isJa ? '間もなく終了' : 'Ending Soon'}
              </span>
            )}
            {expired && (
              <span className="text-[9px] font-extrabold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {isJa ? '終了' : 'Ended'}
              </span>
            )}
          </div>
        </div>

        {/* title */}
        <h3 className="font-extrabold text-[#1a3d20] text-[17px] leading-snug mb-2 line-clamp-2">
          {isJa ? notice.titleJa : notice.titleEn}
        </h3>

        {/* body */}
        <p className="text-[#555] text-xs leading-relaxed line-clamp-2 mb-5">
          {isJa ? notice.bodyJa : notice.bodyEn}
        </p>

        {/* footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-[#1a3d20]/45 text-[10px] font-bold">
            <FiCalendar size={10} />
            {formatDateStr(notice.date, isJa)}
          </div>
          {!expired && (
            <div
              className="flex items-center gap-1 text-[10px] font-extrabold"
              style={{ color: cfg?.color }}
            >
              {isJa ? '詳細を見る' : 'Read more'}
              <FiChevronRight size={11} />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ── DetailModal ─────────────────────────────────────────── */
function DetailModal({ notice, isJa, onClose }) {
  const cfg = NOTICE_TYPES[notice.type];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />

      <motion.div
        className="relative z-10 w-full sm:max-w-lg bg-[#faf7f0] rounded-t-[28px] sm:rounded-[28px] overflow-hidden shadow-2xl flex flex-col"
        style={{ maxHeight: '85vh' }}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* top bar */}
        <div className="h-[4px]" style={{ background: cfg?.color }} />

        {/* header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-[#dfd1bc] shrink-0">
          <div className="flex-1">
            <TypeTag type={notice.type} isJa={isJa} />
            <h3 className="font-extrabold text-[#1a3d20] text-lg leading-snug mt-2">
              {isJa ? notice.titleJa : notice.titleEn}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#1f4d2b]/10 flex items-center justify-center text-[#1f4d2b]/60 hover:bg-[#1f4d2b]/20 transition shrink-0 mt-1"
          >
            <FiX size={14} />
          </button>
        </div>

        {/* body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          <p className="text-[#444] text-sm leading-[1.9]">
            {isJa ? notice.bodyJa : notice.bodyEn}
          </p>

          {/* dates */}
          <div className="mt-6 flex flex-wrap gap-3">
            <div className="flex items-center gap-2 bg-white border border-[#dfd1bc] rounded-xl px-4 py-2.5">
              <FiCalendar size={13} className="text-[#1f4d2b]" />
              <div>
                <p className="text-[9px] font-extrabold tracking-widest uppercase text-[#1a3d20]/40">
                  {isJa ? '掲載日' : 'Posted'}
                </p>
                <p className="text-xs font-bold text-[#1a3d20]">
                  {formatDateStr(notice.date, isJa)}
                </p>
              </div>
            </div>

            {notice.expires && (
              <div className="flex items-center gap-2 bg-white border border-[#dfd1bc] rounded-xl px-4 py-2.5">
                <FiClock size={13} className="text-[#1f4d2b]" />
                <div>
                  <p className="text-[9px] font-extrabold tracking-widest uppercase text-[#1a3d20]/40">
                    {isJa ? '有効期限' : 'Expires'}
                  </p>
                  <p className="text-xs font-bold text-[#1a3d20]">
                    {formatDateStr(notice.expires, isJa)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* footer */}
        <div className="px-6 py-4 border-t border-[#dfd1bc] shrink-0 flex items-center justify-between gap-3">
          <a
            href="tel:0537269489"
            className="text-[#1f4d2b] text-xs font-bold hover:underline"
          >
            📞 0537-26-9489
          </a>

          <div className="flex items-center gap-2">
            {/* ★ website button — only shown when notice has a link */}
            {notice.link && (
              <a
                href={notice.link}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  inline-flex items-center gap-1.5
                  bg-[#ea622a] hover:bg-[#d45520]
                  text-white text-xs font-extrabold
                  px-5 py-2.5 rounded-full
                  transition no-underline
                "
              >
                <FiExternalLink size={12} />
                {isJa ? 'サイトを見る' : 'Visit Website'}
              </a>
            )}

            <button
              onClick={onClose}
              className="bg-[#1f4d2b] hover:bg-[#2d6b38] text-white text-xs font-extrabold px-6 py-2.5 rounded-full transition"
            >
              {isJa ? '閉じる' : 'Close'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ── MAIN ────────────────────────────────────────────────── */
const FILTER_KEYS = ['all', 'campaign', 'discount', 'holiday', 'info'];

export default function NoticeBoard() {
  const { i18n } = useTranslation();
  const isJa = i18n.language?.startsWith('ja');

  const [activeFilter, setActiveFilter] = useState('all');
  const [selected,     setSelected]     = useState(null);

  const sorted = useMemo(() => {
    return [...NOTICES].sort((a, b) => {
      if (a.important !== b.important) return a.important ? -1 : 1;
      return new Date(b.date) - new Date(a.date);
    });
  }, []);

  const filtered = useMemo(() => {
    if (activeFilter === 'all') return sorted;
    return sorted.filter((n) => n.type === activeFilter);
  }, [activeFilter, sorted]);

  const filterLabel = (key) => {
    if (key === 'all') return isJa ? 'すべて' : 'All';
    const cfg = NOTICE_TYPES[key];
    return cfg ? `${cfg.emoji} ${isJa ? cfg.labelJa : cfg.labelEn}` : key;
  };

  return (
    <section id="notices" className="relative py-20 bg-[#f4f9ee] overflow-hidden">

      {/* bg deco */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle,rgba(31,77,43,0.04) 1px,transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      <div className="absolute top-10 right-10 text-[120px] opacity-[0.04] pointer-events-none select-none leading-none">🌿</div>
      <div className="absolute bottom-10 left-10 text-[100px] opacity-[0.04] pointer-events-none select-none leading-none">🍃</div>

      <div className="max-w-6xl mx-auto px-5 relative z-10">

        {/* header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-[#5b3a1c] text-white px-7 py-3 rounded-2xl shadow-lg border-2 border-[#7a5230] mb-5">
            <span className="text-xl">📋</span>
            <span className="font-extrabold text-lg tracking-wide">
              {isJa ? 'お知らせ' : 'Notice Board'}
            </span>
          </div>

          <h2
            className="font-extrabold text-[#1a3d20] mb-3"
            style={{ fontSize: 'clamp(2rem,5vw,3.5rem)', lineHeight: 1.1 }}
          >
            {isJa ? '最新情報・キャンペーン' : 'Latest News & Campaigns'}
          </h2>

          <p className="text-[#2d6b38]/70 text-base font-semibold max-w-md mx-auto">
            {isJa
              ? 'キャンペーン・割引・営業変更などの最新情報をお届けします'
              : 'Campaigns, discounts, holiday hours and park updates'}
          </p>
        </motion.div>

        {/* FILTERS */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="flex flex-wrap gap-2 justify-center mb-10"
        >
          {FILTER_KEYS.map((key) => {
            const isActive = activeFilter === key;
            const cfg = NOTICE_TYPES[key];
            return (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                className={`
                  px-5 py-2.5 rounded-full text-xs font-extrabold tracking-wide
                  border-[1.5px] transition-all duration-200
                  ${isActive
                    ? 'text-white shadow-lg'
                    : 'bg-white text-[#1a3d20]/60 border-[#dfd1bc] hover:border-[#1f4d2b]/40 hover:text-[#1f4d2b]'}
                `}
                style={isActive ? { background: cfg?.color || '#1f4d2b', borderColor: cfg?.color || '#1f4d2b' } : {}}
              >
                {filterLabel(key)}
              </button>
            );
          })}
        </motion.div>

        {/* cards */}
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((notice) => (
              <NoticeCard
                key={notice.id}
                notice={notice}
                isJa={isJa}
                onClick={setSelected}
              />
            ))}
          </AnimatePresence>
        </motion.div>

        {/* bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-12 bg-white border-[1.5px] border-[#dfd1bc] rounded-[24px] px-8 py-6 flex flex-wrap items-center justify-between gap-5 shadow-sm"
        >
          <div className="flex items-center gap-4">
            <div className="text-4xl">📞</div>
            <div>
              <p className="font-extrabold text-[#1a3d20] text-base">
                {isJa ? 'キャンペーンの詳細はお電話で' : 'Call us for campaign details'}
              </p>
              <p className="text-[#555] text-xs mt-0.5">
                {isJa ? '最新情報・予約はお気軽にどうぞ' : 'Latest info & reservations welcome'}
              </p>
            </div>
          </div>
          <a
            href="tel:0537269489"
            className="flex items-center gap-2 bg-[#1f4d2b] hover:bg-[#2d6b38] text-white font-extrabold text-sm px-7 py-3.5 rounded-full transition shadow-lg shadow-[#1f4d2b]/20 no-underline"
          >
            📞 0537-26-9489
          </a>
        </motion.div>

      </div>

      {/* modal */}
      <AnimatePresence>
        {selected && (
          <DetailModal
            notice={selected}
            isJa={isJa}
            onClose={() => setSelected(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}